import Phaser from 'phaser';
import { GameService, GameCompletionResult } from '../../../../services/gameService';
import { eventApi } from '../../../../services/api';
import gamePopupService from '../../../../services/gamePopupService';

export class KilobaRacingScene extends Phaser.Scene {
  private gameState: 'start' | 'playing' | 'tutorial' | 'gameover' = 'start';
  private score: number = 0;
  private scoreMultiplier: number = 1;
  private scoreText!: Phaser.GameObjects.Text;
  private speedText!: Phaser.GameObjects.Text;
  private character!: Phaser.GameObjects.Image;
  private characterLane: number = 1; // 0, 1, 2 (left, center, right)
  private roadSpeed: number = 400;
  private obstacles!: Phaser.GameObjects.Group;
  private roads!: Phaser.GameObjects.Group;
  private lastObstacleTime: number = 0;
  private gameOverText!: Phaser.GameObjects.Text;
  private playAgainText!: Phaser.GameObjects.Text;
  private isApiCallInProgress: boolean = false;
  
  // Tutorial system
  private isTutorialMode: boolean = false;
  private tutorialStep: 'left' | 'right' | 'completed' = 'left';
  private tutorialOverlay!: Phaser.GameObjects.Rectangle;
  private tutorialText!: Phaser.GameObjects.Text;
  private tutorialObstacles!: Phaser.GameObjects.Group;
  private originalRoadSpeed: number = 400;
  
  // Event session tracking
  private gameSessionId: string | null = null;
  private isEventMode: boolean = false;
  
  // Dynamic screen dimensions
  private screenWidth: number = 0;
  private screenHeight: number = 0;
  private centerX: number = 0;
  private centerY: number = 0;
  
  // Lane positions (will be calculated based on screen width)
  private LANE_POSITIONS: number[] = [];
  private readonly LANE_WIDTH_RATIO = 0.167; // 180/1080 = 0.167 (relative to screen width)
  
  // Obstacle types for random spawning
  private readonly OBSTACLE_TYPES: string[] = ['giay', 'loc', 'stone', 'hacker'];

  constructor() {
    super({ key: 'KilobaRacingScene' });
  }

  preload(): void {
    // Assets are already loaded by RacingLoadingScene
    // No need to load again here
    // this.load.font('Arial', '../../../../assets/fonts/SVN-Gilroy Medium.otf');
    this.load.font('SVN-Gilroy', '/SVN-Gilroy-Medium.otf', 'opentype');
  }

  private checkEventMode(): void {
    // Check URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('gameSessionId');
    
    // Also check session storage as backup
    const storedSessionId = sessionStorage.getItem('gameSessionId');
    
    // Check if running from EventQRScanPage
    const isFromEventQRPage = window.location.pathname.includes('/event-qr-scan');
    
    console.log('🔍 Event Mode Detection:');
    console.log('  URL gameSessionId:', sessionId);
    console.log('  Session storage gameSessionId:', storedSessionId);
    console.log('  Current URL:', window.location.href);
    console.log('  Is from event-qr-scan page:', isFromEventQRPage);
    
    if (sessionId || storedSessionId || isFromEventQRPage) {
      this.gameSessionId = sessionId || storedSessionId;
      this.isEventMode = true;
      console.log('✅ Event mode detected, gameSessionId:', this.gameSessionId);
    } else {
      this.isEventMode = false;
      console.log('❌ Regular game mode (no gameSessionId found)');
    }
  }

  private checkTutorialNeeded(): boolean {
    try {
      const tutorialCompleted = localStorage.getItem('racing_tutorial');
      return tutorialCompleted !== 'completed';
    } catch (error) {
      console.warn('LocalStorage not available, skipping tutorial:', error);
      return false;
    }
  }

  private startTutorial(): void {
    console.log('🎯 Starting tutorial mode');
    this.isTutorialMode = true;
    this.tutorialStep = 'left';
    this.gameState = 'tutorial';
    
    // Slow down the game significantly for tutorial
    this.originalRoadSpeed = this.roadSpeed;
    this.roadSpeed = 150; // Much slower than normal 400
    
    // Start the game with tutorial
    this.startGame();
    
    // Add tutorial UI
    this.createTutorialUI();
  }

  create(): void {
    // Check for event mode - look for gameSessionId in URL params or session storage
    this.checkEventMode();
    
    // Reset game completion status for new session
    this.data.set('gameCompleted', false);
    
    // Get dynamic screen dimensions
    this.screenWidth = this.cameras.main.width;
    this.screenHeight = this.cameras.main.height;
    this.centerX = this.screenWidth / 2;
    this.centerY = this.screenHeight / 2;
    
    // Calculate lane positions based on screen width
    const laneWidth = this.screenWidth * this.LANE_WIDTH_RATIO;
    this.LANE_POSITIONS = [
      this.centerX - laneWidth, // Left lane
      this.centerX,             // Center lane  
      this.centerX + laneWidth  // Right lane
    ];
    
    // Initialize groups
    this.obstacles = this.add.group();
    this.roads = this.add.group();
    this.tutorialObstacles = this.add.group();
    
    // Setup physics with dynamic bounds
    this.physics.world.setBounds(0, 0, this.screenWidth, this.screenHeight);
    
    // Optimize renderer settings for better quality
    this.optimizeRendering();
    
    // Create fallback graphics for missing assets
    this.createFallbackAssets();
    
    // Start with start screen
    this.showStartScreen();
    
    // Setup input handlers
    this.setupInputHandlers();
  }

  private optimizeRendering(): void {
    // Enable high quality rendering
    if (this.renderer) {
      const renderer = this.renderer as Phaser.Renderer.WebGL.WebGLRenderer;
      if (renderer.gl) {
        // Enable anisotropic filtering if available
        const ext = renderer.gl.getExtension('EXT_texture_filter_anisotropic');
        if (ext) {
          renderer.gl.texParameterf(renderer.gl.TEXTURE_2D, ext.TEXTURE_MAX_ANISOTROPY_EXT, 4);
        }
      }
    }
    
    // Optimize camera for better rendering
    this.cameras.main.roundPixels = false;
    this.cameras.main.useBounds = true;
  }

  private createFallbackAssets(): void {
    // Create simple colored rectangles as fallbacks
    if (!this.textures.exists('way')) {
      const graphics = this.add.graphics();
      // Calculate road size based on screen dimensions
      const fallbackWidth = this.screenWidth * 0.51; // ~55% of screen width (552/1080 = 0.51)
      const fallbackHeight = this.screenHeight;
      
      graphics.fillStyle(0x333333);
      graphics.fillRect(0, 0, fallbackWidth, fallbackHeight);
      graphics.lineStyle(3, 0xffffff);
      // Draw lane lines proportionally
      graphics.lineBetween(fallbackWidth * 0.33, 0, fallbackWidth * 0.33, fallbackHeight);
      graphics.lineBetween(fallbackWidth * 0.67, 0, fallbackWidth * 0.67, fallbackHeight);
      graphics.generateTexture('way_fallback', fallbackWidth, fallbackHeight);
      graphics.destroy();
    }
    
    if (!this.textures.exists('character')) {
      const graphics = this.add.graphics();
      // Character aspect ratio: 136x230 (original size)
      const originalWidth = 136;
      const originalHeight = 230;
      const aspectRatio = originalWidth / originalHeight; // 0.591
      
      // Scale based on screen height while maintaining aspect ratio
      const scaledHeight = this.screenHeight * 0.12; // 12% of screen height
      const scaledWidth = scaledHeight * aspectRatio;
      
      graphics.fillStyle(0x00ff00);
      graphics.fillRoundedRect(0, 0, scaledWidth, scaledHeight, 10);
      // Add some details to make it look more like a character
      graphics.fillStyle(0x333333);
      graphics.fillCircle(scaledWidth/2, scaledHeight * 0.25, scaledWidth * 0.11); // head
      graphics.generateTexture('character_fallback', scaledWidth, scaledHeight);
      graphics.destroy();
    }
    
    if (!this.textures.exists('giay')) {
      const graphics = this.add.graphics();
      // Scale obstacle size based on screen dimensions
      const obstacleWidth = this.screenWidth * 0.132; // 143/1080 = 0.132
      const obstacleHeight = this.screenHeight * 0.077; // 147/1920 = 0.077
      graphics.fillStyle(0xff0000);
      graphics.fillRoundedRect(0, 0, obstacleWidth, obstacleHeight, 10);
      graphics.generateTexture('giay_fallback', obstacleWidth, obstacleHeight);
      graphics.destroy();
    }
    
    // Create fallback textures for other obstacle types
    if (!this.textures.exists('loc')) {
      const graphics = this.add.graphics();
      const obstacleWidth = this.screenWidth * 0.132;
      const obstacleHeight = this.screenHeight * 0.077;
      graphics.fillStyle(0x8B4513); // Brown color for loc (barrel)
      graphics.fillRoundedRect(0, 0, obstacleWidth, obstacleHeight, 10);
      graphics.generateTexture('loc_fallback', obstacleWidth, obstacleHeight);
      graphics.destroy();
    }
    
    if (!this.textures.exists('stone')) {
      const graphics = this.add.graphics();
      const obstacleWidth = this.screenWidth * 0.132;
      const obstacleHeight = this.screenHeight * 0.077;
      graphics.fillStyle(0x808080); // Gray color for stone
      graphics.fillRoundedRect(0, 0, obstacleWidth, obstacleHeight, 5);
      graphics.generateTexture('stone_fallback', obstacleWidth, obstacleHeight);
      graphics.destroy();
    }
    
    if (!this.textures.exists('hacker')) {
      const graphics = this.add.graphics();
      const obstacleWidth = this.screenWidth * 0.132;
      const obstacleHeight = this.screenHeight * 0.077;
      graphics.fillStyle(0x000000); // Black color for hacker
      graphics.fillRoundedRect(0, 0, obstacleWidth, obstacleHeight, 10);
      graphics.fillStyle(0x00ff00); // Green accent
      graphics.fillRect(obstacleWidth * 0.1, obstacleHeight * 0.1, obstacleWidth * 0.8, obstacleHeight * 0.1);
      graphics.generateTexture('hacker_fallback', obstacleWidth, obstacleHeight);
      graphics.destroy();
    }
    
    if (!this.textures.exists('play_button')) {
      const graphics = this.add.graphics();
      const buttonWidth = this.screenWidth * 0.0925; // 200/1080 = 0.185, halved to 0.0925
      const buttonHeight = this.screenHeight * 0.021; // 80/1920 = 0.042, halved to 0.021
      graphics.fillStyle(0x4CAF50);
      graphics.fillRoundedRect(0, 0, buttonWidth, buttonHeight, 20);
      graphics.generateTexture('play_button_fallback', buttonWidth, buttonHeight);
      graphics.destroy();
    }

  }

  private showStartScreen(): void {
    console.log('showStartScreen() called');
    this.gameState = 'start';
    
    // Clear previous elements
    this.children.removeAll();
    this.obstacles.clear(true, true);
    this.roads.clear(true, true);
    
    // Add blue background color (#062B9C)
    const background = this.add.rectangle(this.centerX, this.centerY, this.screenWidth, this.screenHeight, 0x062B9C);
    
    // Add start background scaled to fit screen while maintaining aspect ratio
    const startBg = this.add.image(this.centerX, this.screenHeight, 'background_start');
    // Set origin to center horizontally, bottom vertically
    startBg.setOrigin(0.5, 1);
    // Scale to fit screen width: original 880x1912, scale to current screen width
    const scaleRatio = this.screenWidth / 880; // Scale factor to fit width
    const scaledHeight = 1912 * scaleRatio; // Maintain aspect ratio
    startBg.setDisplaySize(this.screenWidth, scaledHeight);
    // Enable smoothing for better image quality
    startBg.setTexture(startBg.texture.key);
    
    // Add play button
    const playButtonTexture = this.textures.exists('play_button') ? 'play_button' : 'play_button_fallback';
    const playButton = this.add.image(this.centerX, this.screenHeight * 0.75, playButtonTexture);
    // Scale button to half size (120x120 instead of 200x200)
    playButton.setDisplaySize(376, 212);
    playButton.setInteractive();
    playButton.on('pointerdown', () => {
      // Only start game if we're actually on start screen
      if (this.gameState === 'start') {
        this.startGame();
      }
    });
  }

  private startGame(): void {
    console.log('startGame() called - current state:', this.gameState);
    
    // Check if tutorial is needed before starting regular game
    if (!this.isTutorialMode && this.checkTutorialNeeded()) {
      this.startTutorial();
      return;
    }
    
    this.gameState = this.isTutorialMode ? 'tutorial' : 'playing';
    this.score = 0;
    this.scoreMultiplier = 1;
    this.roadSpeed = this.isTutorialMode ? 150 : 400; // Slow for tutorial, normal for regular game
    this.characterLane = 1;
    this.lastObstacleTime = 0;
    
    // Clear previous elements
    this.children.removeAll();
    this.obstacles.clear(true, true);
    this.roads.clear(true, true);
    if (this.tutorialObstacles) {
      this.tutorialObstacles.clear(true, true);
    }
    
    // Add blue background color (#062B9C)
    const background = this.add.rectangle(this.centerX, this.centerY, this.screenWidth, this.screenHeight, 0x062B9C);
    
    // Add game background
    const gameBg = this.add.image(this.centerX, this.centerY, 'background_2');
    gameBg.setDisplaySize(this.screenWidth, this.screenHeight);
    
    // Create initial roads
    this.createInitialRoads();
    
    // Add character (positioned at upper part of screen)
    const characterTexture = this.textures.exists('character') ? 'character' : 'character_fallback';
    this.character = this.add.image(this.LANE_POSITIONS[1], this.screenHeight * 0.21, characterTexture);
    // Scale character while maintaining aspect ratio (136x230 = 0.591 ratio)
    const originalAspectRatio = 136 / 230; // 0.591
    const scaledHeight = this.screenHeight * 0.12; // 12% of screen height
    const scaledWidth = scaledHeight * originalAspectRatio;
    this.character.setDisplaySize(scaledWidth, scaledHeight);
    
    // Add score text
    const fontSize = Math.max(this.screenWidth * 0.044, 24); // 48/1080 = 0.044, minimum 24px
    this.scoreText = this.add.text(this.screenWidth * 0.046, this.screenHeight * 0.026, 'Điểm: 0', {
      fontSize: `${fontSize}px`,
      color: '#ffffff',
      fontFamily: 'gilroy-medium',
      stroke: '#000000',
      strokeThickness: 2,
      resolution: window.devicePixelRatio || 1
    });
    
    // Add speed indicator
    const speedFontSize = Math.max(this.screenWidth * 0.033, 18); // 36/1080 = 0.033, minimum 18px
    // this.speedText = this.add.text(this.screenWidth * 0.046, this.screenHeight * 0.063, 'Tốc độ: 1x', {
    //   fontSize: `${speedFontSize}px`,
    //   color: '#ffffff',
    //   fontFamily: 'gilroy-medium',
    //   stroke: '#000000',
    //   strokeThickness: 2,
    //   resolution: window.devicePixelRatio || 1
    // });
    
    // Add back button in top-left corner
    
    
    // Start score timer (faster interval for more challenging scoring)
    this.time.addEvent({
      delay: 600, // Reduced from 700ms to 600ms (every 0.6 seconds for even faster progression)
      callback: this.updateScore,
      callbackScope: this,
      loop: true
    });
    
    // Create tutorial UI if in tutorial mode
    if (this.isTutorialMode) {
      this.createTutorialUI();
    }
  }

  private createTutorialUI(): void {
    // Create tutorial overlay and text
    this.showTutorialStep();
    
    // Create tutorial obstacles to demonstrate avoidance
    this.createTutorialObstacles();
  }

  private createTutorialObstacles(): void {
    // Clear any existing tutorial obstacles
    this.tutorialObstacles.clear(true, true);
    
    // Create static obstacles to demonstrate the need to avoid them
    const obstacleWidth = this.screenWidth * 0.132;
    const obstacleHeight = this.screenHeight * 0.077;
    
    if (this.tutorialStep === 'left') {
      // Place obstacles in center and right lanes to force movement to left
      // Center lane obstacle
      const centerObstacle = this.add.image(this.LANE_POSITIONS[1], this.screenHeight * 0.45, 'giay');
      centerObstacle.setDisplaySize(obstacleWidth, obstacleHeight);
      this.tutorialObstacles.add(centerObstacle);
      
      // Right lane obstacle
      const rightObstacle = this.add.image(this.LANE_POSITIONS[2], this.screenHeight * 0.35, 'loc');
      rightObstacle.setDisplaySize(obstacleWidth, obstacleHeight);
      this.tutorialObstacles.add(rightObstacle);
      
      // Add pulsing effect to obstacles
      this.tweens.add({
        targets: [centerObstacle, rightObstacle],
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 1000,
        yoyo: true,
        repeat: -1,
        ease: 'Power2'
      });
      
    } else if (this.tutorialStep === 'right') {
      // Place obstacles in left and center lanes to force movement to right
      // Left lane obstacle  
      const leftObstacle = this.add.image(this.LANE_POSITIONS[0], this.screenHeight * 0.45, 'stone');
      leftObstacle.setDisplaySize(obstacleWidth, obstacleHeight);
      this.tutorialObstacles.add(leftObstacle);
      
      // Center lane obstacle
      const centerObstacle = this.add.image(this.LANE_POSITIONS[1], this.screenHeight * 0.35, 'hacker');
      centerObstacle.setDisplaySize(obstacleWidth, obstacleHeight);
      this.tutorialObstacles.add(centerObstacle);
      
      // Add pulsing effect to obstacles
      this.tweens.add({
        targets: [leftObstacle, centerObstacle],
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 1000,
        yoyo: true,
        repeat: -1,
        ease: 'Power2'
      });
    }
  }

  private showTutorialStep(): void {
    // Remove existing tutorial UI if any
    if (this.tutorialOverlay) {
      this.tutorialOverlay.destroy();
    }
    if (this.tutorialText) {
      this.tutorialText.destroy();
    }

    const fontSize = Math.max(this.screenWidth * 0.044, 32); // Large font size for tutorial
    
    if (this.tutorialStep === 'left') {
      // Highlight left half of screen
      this.tutorialOverlay = this.add.rectangle(
        this.screenWidth * 0.25, // Center of left half
        this.centerY,
        this.screenWidth * 0.5, // Half screen width
        this.screenHeight,
        0x3F51B5, // Blue color
        0.6 // More opaque for better visibility
      );
      
      // Add tutorial text for left move
      this.tutorialText = this.add.text(
        this.screenWidth * 0.25, // Center of left half
        this.screenHeight * 0.85, // Near bottom
        'Chạm vào vùng này\nđể di chuyển sang trái\nvà tránh chướng ngại vật',
        {
          fontSize: `${fontSize}px`,
          color: '#ffffff',
          fontFamily: 'gilroy-medium',
          align: 'center',
          stroke: '#000000',
          strokeThickness: 3,
          resolution: window.devicePixelRatio || 1
        }
      );
      this.tutorialText.setOrigin(0.5);
      
      // Make overlay blink to draw attention
      this.tweens.add({
        targets: this.tutorialOverlay,
        alpha: 0.3,
        duration: 800,
        yoyo: true,
        repeat: -1,
        ease: 'Power2'
      });
      
    } else if (this.tutorialStep === 'right') {
      // Highlight right half of screen
      this.tutorialOverlay = this.add.rectangle(
        this.screenWidth * 0.75, // Center of right half
        this.centerY,
        this.screenWidth * 0.5, // Half screen width
        this.screenHeight,
        0xFF5722, // Orange color
        0.6 // More opaque for better visibility
      );
      
      // Add tutorial text for right move
      this.tutorialText = this.add.text(
        this.screenWidth * 0.75, // Center of right half
        this.screenHeight * 0.85, // Near bottom
        'Chạm vào vùng này\nđể di chuyển sang phải\nvà tránh chướng ngại vật',
        {
          fontSize: `${fontSize}px`,
          color: '#ffffff',
          fontFamily: 'gilroy-medium',
          align: 'center',
          stroke: '#000000',
          strokeThickness: 3,
          resolution: window.devicePixelRatio || 1
        }
      );
      this.tutorialText.setOrigin(0.5);
      
      // Make overlay blink to draw attention
      this.tweens.add({
        targets: this.tutorialOverlay,
        alpha: 0.3,
        duration: 800,
        yoyo: true,
        repeat: -1,
        ease: 'Power2'
      });
    }
    
    // Create tutorial obstacles for current step
    this.createTutorialObstacles();
  }

  private completeTutorial(): void {
    console.log('🎯 Tutorial completed!');
    
    // Clean up tutorial UI
    if (this.tutorialOverlay) {
      this.tutorialOverlay.destroy();
    }
    if (this.tutorialText) {
      this.tutorialText.destroy();
    }
    
    // Clean up tutorial obstacles
    this.tutorialObstacles.clear(true, true);
    
    // Save tutorial completion to localStorage
    try {
      localStorage.setItem('racing_tutorial', 'completed');
    } catch (error) {
      console.warn('Could not save tutorial completion to localStorage:', error);
    }
    
    // Reset to normal game mode
    this.isTutorialMode = false;
    this.tutorialStep = 'completed';
    this.gameState = 'playing';
    this.roadSpeed = this.originalRoadSpeed; // Restore normal speed
    
    // Show completion message briefly
    const completionText = this.add.text(
      this.centerX,
      this.centerY,
      'Hướng dẫn hoàn thành!\nBắt đầu chơi!',
      {
        fontSize: `${Math.max(this.screenWidth * 0.05, 36)}px`,
        color: '#EA5C15',
        fontFamily: 'gilroy-medium',
        align: 'center',
        stroke: '#000000',
        strokeThickness: 3,
        resolution: window.devicePixelRatio || 1
      }
    );
    completionText.setOrigin(0.5);
    
    // Fade out completion message after 2 seconds
    this.time.delayedCall(2000, () => {
      this.tweens.add({
        targets: completionText,
        alpha: 0,
        duration: 500,
        onComplete: () => {
          completionText.destroy();
        }
      });
    });
  }

  private createInitialRoads(): void {
    // Create multiple road segments for scrolling effect (moving upward)
    // Scale road based on screen dimensions
    const roadTexture = this.textures.exists('way') ? 'way' : 'way_fallback';
    const roadWidth = this.screenWidth * 0.51; // 552/1080 = 0.51
    const roadHeight = this.screenHeight;
    
    for (let i = 0; i < 4; i++) {
      // Position roads to cover the character area (character is at screenHeight * 0.21)
      // Start first road at center of screen so it covers character position
      const road = this.add.image(this.centerX, this.centerY + i * roadHeight, roadTexture);
      road.setDisplaySize(roadWidth, roadHeight);
      this.roads.add(road);
    }
  }

  private setupInputHandlers(): void {
    // Touch/click input for screen areas
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Debug log to check current state
      console.log('Pointer down - Game state:', this.gameState, 'Click X:', pointer.x);
      
      if (this.gameState === 'gameover') {
        // Không cho phép chơi lại nếu đang trong quá trình xử lý API
        if (this.isApiCallInProgress) {
          console.log('API call in progress, ignoring play again request');
          return;
        }
        this.showStartScreen();
        return;
      }
      
      // Handle start screen input
      if (this.gameState === 'start') {
        // Let the play button handle this, don't interfere
        return;
      }
      
      if (this.gameState === 'playing') {
        const screenWidth = this.screenWidth;
        const clickX = pointer.x;
        
        // Divide screen into 3 zones for better control
        const leftZone = screenWidth * 0.33;   // 0 to 33% of screen width
        const rightZone = screenWidth * 0.67;  // 67% to 100% of screen width
        // Middle zone: 33% to 67% (no action to avoid accidental moves)
        
        if (clickX < leftZone) {
          // Left zone - move left
          this.showTapFeedback('left', pointer.x, pointer.y);
          this.moveLeft();
        } else if (clickX > rightZone) {
          // Right zone - move right
          this.showTapFeedback('right', pointer.x, pointer.y);
          this.moveRight();
        }
        // Middle zone: do nothing (prevents accidental moves)
      }
      
      // Handle tutorial mode input
      if (this.gameState === 'tutorial') {
        const screenWidth = this.screenWidth;
        const clickX = pointer.x;
        
        if (this.tutorialStep === 'left' && clickX < screenWidth * 0.5) {
          // User clicked on left side during left tutorial step
          this.showTapFeedback('left', pointer.x, pointer.y);
          this.moveLeft();
          
          // Move to next tutorial step
          this.tutorialStep = 'right';
          this.showTutorialStep();
          
        } else if (this.tutorialStep === 'right' && clickX > screenWidth * 0.5) {
          // User clicked on right side during right tutorial step
          this.showTapFeedback('right', pointer.x, pointer.y);
          this.moveRight();
          
          // Complete tutorial
          this.completeTutorial();
        }
      }
    });

    // Keyboard controls for testing/desktop
    this.input.keyboard?.on('keydown-LEFT', () => {
      if (this.gameState === 'playing') {
        this.moveLeft();
      } else if (this.gameState === 'tutorial') {
        if (this.tutorialStep === 'left') {
          this.moveLeft();
          this.tutorialStep = 'right';
          this.showTutorialStep();
        }
      }
    });
    
    this.input.keyboard?.on('keydown-RIGHT', () => {
      if (this.gameState === 'playing') {
        this.moveRight();
      } else if (this.gameState === 'tutorial') {
        if (this.tutorialStep === 'right') {
          this.moveRight();
          this.completeTutorial();
        }
      }
    });
  }

  private showTapFeedback(side: 'left' | 'right', x: number, y: number): void {
    // Create a visual feedback effect at tap location
    const circleRadius = this.screenWidth * 0.028; // 30/1080 = 0.028
    const circle = this.add.circle(x, y, circleRadius, side === 'left' ? 0x3F51B5 : 0xFF5722, 0.6);
    
    this.tweens.add({
      targets: circle,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      duration: 300,
      ease: 'Power2',
      onComplete: () => {
        circle.destroy();
      }
    });
  }

  private moveLeft(): void {
    if (this.characterLane > 0) {
      this.characterLane--;
      this.tweens.add({
        targets: this.character,
        x: this.LANE_POSITIONS[this.characterLane],
        duration: 200,
        ease: 'Power2'
      });
      
      // Add visual feedback - scale effect
      this.tweens.add({
        targets: this.character,
        scaleX: 0.9,
        scaleY: 0.9,
        duration: 100,
        yoyo: true,
        ease: 'Power2'
      });
    }
  }

  private moveRight(): void {
    if (this.characterLane < 2) {
      this.characterLane++;
      this.tweens.add({
        targets: this.character,
        x: this.LANE_POSITIONS[this.characterLane],
        duration: 200,
        ease: 'Power2'
      });
      
      // Add visual feedback - scale effect
      this.tweens.add({
        targets: this.character,
        scaleX: 0.9,
        scaleY: 0.9,
        duration: 100,
        yoyo: true,
        ease: 'Power2'
      });
    }
  }

  private updateScore(): void {
    if (this.gameState === 'playing' && !this.isTutorialMode) {
      // Reduced score increment by half
      this.score += Math.floor(25 * this.scoreMultiplier); // Reduced from 50 to 25
      this.scoreText.setText(`Điểm: ${this.score}`);
      
      // Increase road speed more aggressively for harder gameplay
      this.roadSpeed += 4; // Increased from 3 to 4
      
      // Update speed multiplier and display (faster progression)
      this.scoreMultiplier = 1 + Math.floor((this.roadSpeed - 400) / 80) * 0.3; // Base speed is 400
      const speedLevel = Math.floor((this.roadSpeed - 400) / 40) + 1; // Base speed is 400
      // this.speedText.setText(`Tốc độ: ${speedLevel}x`);
    }
  }

  private spawnObstacle(): void {
    const currentTime = this.time.now;
    
    // Calculate spawn interval based on character height for proper spacing
    const characterHeight = this.screenHeight * 0.12; // Character height is 12% of screen height
    const minObstacleSpacing = characterHeight * 2.5; // Minimum 2.5x character height between obstacle groups
    
    // Convert spacing to time interval based on road speed
    const baseInterval = (minObstacleSpacing / this.roadSpeed) * 1000; // Convert to milliseconds
    const speedReduction = Math.min((this.roadSpeed - 400) * 2.5, 250); // Base speed is 400
    const spawnInterval = Math.max(baseInterval - speedReduction, 350); // Reduced minimum from 400ms to 350ms
    
    if (currentTime - this.lastObstacleTime > spawnInterval) {
      // Get currently occupied lanes near spawn area to avoid blocking all lanes
      const occupiedLanes = this.getOccupiedLanesNearSpawn();
      const availableLanes = [0, 1, 2].filter(lane => !occupiedLanes.includes(lane));
      
      // Determine number of obstacles to spawn (but ensure at least 1 lane remains free)
      let obstacleCount = this.roadSpeed > 500 ? Phaser.Math.Between(1, 2) : 1;
      
      // If spawning multiple obstacles, ensure we don't block all lanes
      if (obstacleCount > 1 && availableLanes.length < 2) {
        obstacleCount = 1; // Limit to 1 obstacle if not enough free lanes
      }
      
      // Select lanes for obstacles ensuring fairness
      const selectedLanes: number[] = [];
      
      if (availableLanes.length === 0) {
        // Emergency case: if all lanes occupied, pick random lane (shouldn't happen with proper spacing)
        selectedLanes.push(Phaser.Math.Between(0, 2));
      } else {
        // Select from available lanes
        for (let i = 0; i < obstacleCount && selectedLanes.length < 2; i++) {
          let attempts = 0;
          let selectedLane: number;
          
          do {
            selectedLane = availableLanes[Phaser.Math.Between(0, availableLanes.length - 1)];
            attempts++;
          } while (selectedLanes.includes(selectedLane) && attempts < 10);
          
          if (!selectedLanes.includes(selectedLane)) {
            selectedLanes.push(selectedLane);
          }
        }
      }
      
      // Spawn obstacles in selected lanes
      selectedLanes.forEach((lane, index) => {
        // Randomly select one of the 4 obstacle types
        const randomObstacleType = this.OBSTACLE_TYPES[Phaser.Math.Between(0, this.OBSTACLE_TYPES.length - 1)];
        const obstacleTexture = this.textures.exists(randomObstacleType) ? randomObstacleType : `${randomObstacleType}_fallback`;
        
        // Calculate minimum spacing based on character height
        const characterHeight = this.screenHeight * 0.12; // Character height is 12% of screen height
        const minSpacing = characterHeight * 2; // Minimum 2x character height spacing
        const maxSpacing = characterHeight * 3; // Maximum 3x character height spacing
        // Add vertical spacing between multiple obstacles
        const yOffset = index * Phaser.Math.Between(minSpacing, maxSpacing);
        const obstacle = this.add.image(this.LANE_POSITIONS[lane], this.screenHeight + 100 + yOffset, obstacleTexture);
        // Scale obstacle size relative to screen
        const obstacleWidth = this.screenWidth * 0.132; // 143/1080 = 0.132
        const obstacleHeight = this.screenHeight * 0.077; // 147/1920 = 0.077
        obstacle.setDisplaySize(obstacleWidth, obstacleHeight);
        
        this.obstacles.add(obstacle);
      });
      
      this.lastObstacleTime = currentTime;
    }
  }

  private getOccupiedLanesNearSpawn(): number[] {
    const occupiedLanes: number[] = [];
    const characterHeight = this.screenHeight * 0.12; // Character height is 12% of screen height
    const spawnCheckZone = characterHeight * 4; // Check zone should be 4x character height to ensure proper spacing
    
    this.obstacles.children.entries.forEach((obstacle: any) => {
      if (!obstacle || !obstacle.active) return;
      
      // Check if obstacle is in spawn zone
      if (obstacle.y > this.screenHeight - spawnCheckZone) {
        // Determine which lane this obstacle is in
        for (let i = 0; i < this.LANE_POSITIONS.length; i++) {
          const laneDistance = Math.abs(obstacle.x - this.LANE_POSITIONS[i]);
          if (laneDistance < this.screenWidth * 0.08) { // Within lane tolerance
            if (!occupiedLanes.includes(i)) {
              occupiedLanes.push(i);
            }
            break;
          }
        }
      }
    });
    
    return occupiedLanes;
  }

  private checkCollisions(): void {
    // Safety check to prevent errors
    if (!this.character || this.gameState !== 'playing') {
      return;
    }
    
    this.obstacles.children.entries.forEach((obstacle: any) => {
      if (!obstacle || !obstacle.active) {
        return;
      }
      
      const distance = Phaser.Math.Distance.Between(
        this.character.x, this.character.y,
        obstacle.x, obstacle.y
      );
      
      // Adjusted collision radius for responsive sizes
      // Scale collision radius based on screen size
      const collisionRadius = this.screenWidth * 0.083; // 90/1080 = 0.083
      if (distance < collisionRadius) { 
        // Add collision effect
        this.cameras.main.shake(200, 0.02);
        this.gameOver();
      }
    });
  }

  private async gameOver(): Promise<void> {
    console.log('gameOver() called - collision detected');
    this.gameState = 'gameover';
    
    // Stop all tweens
    this.tweens.killAll();
    
    // Don't clear the screen - just add overlay and popup
    // Add semi-transparent dark overlay to dim the game screen
    const overlay = this.add.rectangle(this.centerX, this.centerY, this.screenWidth, this.screenHeight, 0x000000, 0.7);
    
    // Add game over background with responsive sizing while maintaining aspect ratio
    const gameOverBg = this.add.image(this.centerX, this.centerY, 'gameover');
    // Original game over size: 656x614, aspect ratio = 656/614 = 1.068
    const originalAspectRatio = 656 / 614; // 1.068
    // Scale based on screen width (60% of screen width)
    const scaledWidth = this.screenWidth * 0.607; // 60.7% of screen width
    const scaledHeight = scaledWidth / originalAspectRatio; // Maintain aspect ratio
    gameOverBg.setDisplaySize(scaledWidth, scaledHeight);
    
    // Show final score with left-aligned label and right-aligned score
    const scoreFontSize = 44; // 54/1080 = 0.05, minimum 32px
    
    // Score label (left aligned)
    const scoreLabel = this.add.text(this.centerX - scaledWidth * 0.45, this.centerY + this.screenHeight * 0.047, 'Điểm số:', {
      fontSize: `${scoreFontSize}px`,
      color: '#ffffff',
      fontFamily: 'gilroy-medium',
      resolution: window.devicePixelRatio || 1
    });
    scoreLabel.setOrigin(0, 0.5); // Left aligned
    
    // Score value (right aligned)
    const scoreValue = this.add.text(this.centerX + scaledWidth * 0.45, this.centerY + this.screenHeight * 0.047, `${this.score}`, {
      fontSize: `${scoreFontSize}px`,
      color: '#ffffff',
      fontFamily: 'gilroy-medium',
      resolution: window.devicePixelRatio || 1
    });
    scoreValue.setOrigin(1, 0.5); // Right aligned
    
    // Show loading text while API call is in progress
    const loadingFontSize = Math.max(this.screenWidth * 0.033, 24); // 36/1080 = 0.033, minimum 24px
    const loadingText = this.add.text(this.centerX, this.centerY + this.screenHeight * 0.099, 'Đang gửi điểm...', {
      fontSize: `${loadingFontSize}px`,
      color: '#ffff00',
      fontFamily: 'gilroy-medium',
      
      resolution: window.devicePixelRatio || 1
    });
    loadingText.setOrigin(0.5);
    
    // Submit score to API - always call API for proper result display
    const isFromEventQRPage = window.location.pathname.includes('/event-qr-scan') || window.location.pathname.includes('/join-event');
    
    if (!this.isApiCallInProgress) {
      this.isApiCallInProgress = true;
      
      try {
        let result;
        
        if (this.isEventMode && this.gameSessionId) {
          // Use event-specific API
          const eventResult = await eventApi.submitEventScore(this.gameSessionId, this.score);
          result = eventResult; // eventResult is ApiResponse type
        } else {
          // Use regular game API
          result = await GameService.completeRacingGame(this.score); // result is GameCompletionResult type
        }
        
        // Hide loading text
        loadingText.setVisible(false);
        
        if (result) {
          // Show result based on mode
          if (this.isEventMode) {
            // Event mode - result is ApiResponse type
            const eventApiResult = result as any; // Type assertion for event API response
            
            // Kiểm tra cả data wrapper và direct response
            const responseData = eventApiResult.data || eventApiResult;
            const finalScore = responseData.finalScore || this.score;
            const rewardMessage = responseData.rewardMessage || responseData.message || 'Đã gửi điểm số thành công!';
            
            
            // Ẩn loading text trước
            loadingText.setVisible(false);
            
            // Tạo text object mới cho reward message với word wrap
            const rewardFontSize = Math.max(this.screenWidth * 0.033, 24); // 36/1080 = 0.033, minimum 24px
            const maxTextWidth = scaledWidth * 0.8; // 80% của chiều rộng popup
            
            const rewardText = this.add.text(this.centerX, this.centerY + this.screenHeight * 0.099, rewardMessage, {
              fontSize: `${rewardFontSize}px`,
              color: '#00ff00',
              fontFamily: 'gilroy-medium',
              align: 'center',
              wordWrap: { width: maxTextWidth, useAdvancedWrap: true },
              resolution: window.devicePixelRatio || 1
            });
            rewardText.setOrigin(0.5);
            
            // Check if game should be disabled after this attempt
            let gameCompleted = false;
            if (responseData.voucherResult?.attemptInfo) {
              const attemptInfo = responseData.voucherResult.attemptInfo;
              gameCompleted = attemptInfo.remainingAttempts === 0;
            }
            
            // Disable play again button if no attempts remaining
            if (gameCompleted) {
              // Store completion status in scene data
              this.data.set('gameCompleted', true);
            }
            
            // Always trigger callback for EventQRScanPage to handle UI updates
            if (isFromEventQRPage) {
              const onGameEndCallback = (window as any).onGameEndCallback;
              if (onGameEndCallback && typeof onGameEndCallback === 'function') {
                onGameEndCallback(result);
              }
            }
          } else if (!this.isEventMode) {
            // Regular mode - result is GameCompletionResult type
            const gameResult = result as GameCompletionResult;
            
            // Show energy earned info with left-right layout
            const energyFontSize = 44; // 44/1080 = 0.041, minimum 28px
            
            // Energy label (left aligned)
            const energyLabel = this.add.text(this.centerX - scaledWidth * 0.45, this.centerY + this.screenHeight * 0.11, 'Năng lượng:', {
              fontSize: `${energyFontSize}px`,
              color: '#fff',
              fontFamily: 'gilroy-medium',
              resolution: window.devicePixelRatio || 1
            });
            energyLabel.setOrigin(0, 0.5); // Left aligned
            
            // Energy value (right aligned)
            const energyValue = this.add.text(this.centerX + scaledWidth * 0.45, this.centerY + this.screenHeight * 0.11, `+${gameResult.energyEarned}`, {
              fontSize: `${energyFontSize}px`,
              color: '#fff',
              fontFamily: 'gilroy-medium',
              resolution: window.devicePixelRatio || 1
            });
            energyValue.setOrigin(1, 0.5); // Right aligned

            // Show encouragement popup
            const { remainingAttempts } = gameResult;
            if (typeof remainingAttempts === 'number' && remainingAttempts > 0) {
              let message = '';
              if (remainingAttempts === 1) {
                message = "Chốt hạ nào! Lượt cuối cùng hôm nay, không thử thì tiếc lắm đó 😉";
              } else {
                message = `Hay quá! Nhưng còn ${remainingAttempts} lượt nhận thưởng nữa kìa, thử tiếp nhé?`;
              }

              // Use a timeout to show the popup slightly after the game over screen appears
              this.time.delayedCall(500, () => {
                gamePopupService.showPopup({
                  type: 'info',
                  title: 'Còn lượt chơi!',
                  message: message,
                  actionText: 'OK',
                  onAction: () => {}, // Empty action, just closes the popup
                  dismissible: true,
                });
              });
            }
          }
        } else {
          // Show error message
          loadingText.setText('Lỗi kết nối! Thử lại sau.');
          loadingText.setColor('#ff0000');
          loadingText.setVisible(true);
        }
        
        // Show remaining attempts info (only for regular mode)
        if (result && !this.isEventMode && 'remainingAttempts' in result) {
          const gameResult = result as GameCompletionResult;
          if (typeof gameResult.remainingAttempts === 'number') {
            const attemptsFontSize = Math.max(this.screenWidth * 0.033, 24); // 36/1080 = 0.033, minimum 24px
            const remainingText = this.add.text(this.centerX, this.centerY + this.screenHeight * 0.180, 
              `Lượt chơi còn lại hôm nay: ${gameResult.remainingAttempts}`, {
              fontSize: `${attemptsFontSize}px`,
              color: '#cccccc',
              fontFamily: 'gilroy-medium',
              stroke: '#000000',
              strokeThickness: 1,
              resolution: window.devicePixelRatio || 1
            });
            remainingText.setOrigin(0.5);
          }
        }
      } catch (error) {
        console.error('Error submitting racing game score:', error);
        loadingText.setText('Lỗi kết nối! Thử lại sau.');
        loadingText.setColor('#ff0000');
      } finally {
        this.isApiCallInProgress = false;
      }
    } else {
      // API call is already in progress or was skipped
      console.log('🎯 API call already in progress or was handled by parent component');
      loadingText.setText('Điểm số đã được xử lý!');
      loadingText.setColor('#00ff00');
    }
    
    // Show play again button with background image (only if game not completed)
    const gameCompleted = this.data.get('gameCompleted') || false;
    
    if (!gameCompleted) {
      const playAgainFontSize = Math.max(this.screenWidth * 0.044, 28); // 48/1080 = 0.044, minimum 28px
      
      // Create button background image
      const buttonBgTexture = this.textures.exists('bg_button') ? 'bg_button' : null;
      let playAgainButton;
      
      if (buttonBgTexture) {
        // Use the bg_button.png as background
        playAgainButton = this.add.image(this.centerX, this.centerY + this.screenHeight * 0.340, 'bg_button');
        
        // Scale the button to appropriate size (adjust as needed based on your image dimensions)
        const buttonScale = this.screenWidth * 0.001; // Adjust this scale factor as needed
        playAgainButton.setScale(buttonScale);
        playAgainButton.setAlpha(1);
        
        // Add text on top of the button
        const playAgainText = this.add.text(this.centerX, this.centerY + this.screenHeight * 0.340, 'Chạm để chơi lại', {
          fontSize: `${playAgainFontSize}px`,
          color: '#ffffff',
          fontFamily: 'gilroy-medium',
          stroke: '#000000',
          strokeThickness: 1,
          resolution: window.devicePixelRatio || 1
        });
        playAgainText.setOrigin(0.5);
        
        // Make both button and text interactive
        playAgainButton.setInteractive();
        playAgainButton.on('pointerdown', () => {
          if (this.gameState === 'gameover' && !this.data.get('gameCompleted') && !this.isApiCallInProgress) {
            this.showStartScreen();
          }
        });
        
        // Make both button and text blink together
        this.tweens.add({
          targets: [playAgainButton, playAgainText],
          alpha: 0.5,
          duration: 1000,
          yoyo: true,
          repeat: -1
        });
      } else {
        // Fallback to text-only if image not available
        const playAgain = this.add.text(this.centerX, this.centerY + this.screenHeight * 0.340, 'Chạm để chơi lại', {
          fontSize: `${playAgainFontSize}px`,
          color: '#ffffff',
          fontFamily: 'gilroy-medium',
          stroke: '#000000',
          strokeThickness: 2,
          resolution: window.devicePixelRatio || 1
        });
        playAgain.setOrigin(0.5);
        playAgain.setAlpha(0.7);
        
        // Make text blink
        this.tweens.add({
          targets: playAgain,
          alpha: 0.3,
          duration: 1000,
          yoyo: true,
          repeat: -1
        });
      }
    } else {
      // Show completion message instead of play again button
      const completionFontSize = Math.max(this.screenWidth * 0.044, 28);
      const completionText = this.add.text(this.centerX, this.centerY + this.screenHeight * 0.340, 'Đã hoàn thành tất cả lần chơi!', {
        fontSize: `${completionFontSize}px`,
        color: '#ffff00',
        fontFamily: 'gilroy-medium',
        stroke: '#000000',
        strokeThickness: 2,
        resolution: window.devicePixelRatio || 1
      });
      completionText.setOrigin(0.5);
    }
  }

  update(): void {
    if (this.gameState !== 'playing' && this.gameState !== 'tutorial') return;
    
    // Move roads up (reverse direction)
    this.roads.children.entries.forEach((road: any) => {
      road.y -= this.roadSpeed * this.game.loop.delta / 1000;
      
      // Reset road position when it goes off screen (using dynamic road height)
      // Reset to bottom to create continuous scrolling effect
      if (road.y < -this.screenHeight/2) {
        road.y = this.screenHeight + this.screenHeight * 2; // Place far below for smooth cycling
      }
    });
    
    // Move obstacles up (same direction as roads)
    this.obstacles.children.entries.forEach((obstacle: any) => {
      obstacle.y -= this.roadSpeed * this.game.loop.delta / 1000;
      
      // Remove obstacles that are off screen (top)
      if (obstacle.y < -this.screenHeight * 0.104) { // -200 relative to 1920 height = 0.104
        this.obstacles.remove(obstacle);
        obstacle.destroy();
      }
    });
    
    // Only spawn obstacles and check collisions in normal playing mode, not in tutorial
    if (this.gameState === 'playing' && !this.isTutorialMode) {
      // Spawn new obstacles
      this.spawnObstacle();
      
      // Check collisions
      this.checkCollisions();
    }
  }
}
